/* eslint-disable react/require-default-props */

import { useEffect, useRef, useState } from 'react';
import type { ReactNode, MouseEventHandler } from 'react';

import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import BpkBreakpoint, {
  BREAKPOINTS,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkCloseButton from '@skyscanner-internal/backpack-web/bpk-component-close-button';
import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import ChevronLeftIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/chevron-left';
import ChevronRightIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/chevron-right';
import cx from 'classnames';

import ImageInfo from './ImageInfo';
import ScrollContainer from './ScrollContainer';
import SlidesIndicatorDesktop from './SlidesIndicatorDesktop';
import SlidesIndicatorMobile from './SlidesIndicatorMobile';
import { useKeyboardImageNavigation, useScrollToInitialImage } from './utils';

import type { ImageEntry } from '../types';

import STYLES from './SlideshowView.module.scss';

const RtlSupportedChevronLeftIcon = withRtlSupport(ChevronLeftIcon);
const RtlSupportedChevronRightIcon = withRtlSupport(ChevronRightIcon);

type Props = {
  aspectRatio?: string;
  onClose: () => void;
  images: ImageEntry[];
  initialImageIndex: number;
  translations: {
    closeModal: string;
    previousImage: string;
    nextImage: string;
  };
};

const SlideshowView = ({
  aspectRatio,
  images,
  initialImageIndex = 0,
  onClose,
  translations,
}: Props) => {
  const [shownImageIndex, updateShownImageIndex] = useState(initialImageIndex);
  const imagesRef = useRef<Array<HTMLElement | null>>([]);

  useScrollToInitialImage(initialImageIndex, imagesRef);

  const updateShownImageIndexAndScroll = (newIndex: number) => {
    updateShownImageIndex(newIndex);
    const imageElement = imagesRef.current[newIndex];
    if (imageElement) {
      imageElement.scrollIntoView({
        block: 'nearest',
        inline: 'start',
      });
    }
  };

  const scrollToNext = () => {
    updateShownImageIndexAndScroll(
      shownImageIndex === images.length - 1 ? 0 : shownImageIndex + 1,
    );
  };
  const scrollToPrev = () => {
    updateShownImageIndexAndScroll(
      shownImageIndex === 0 ? images.length - 1 : shownImageIndex - 1,
    );
  };

  const onKeyDown = useKeyboardImageNavigation(scrollToPrev, scrollToNext);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const shownImage = images[shownImageIndex];

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div ref={containerRef} tabIndex={-1} onKeyDown={onKeyDown}>
      <header className={STYLES.header}>
        <BpkCloseButton onClick={onClose} label={translations.closeModal} />
      </header>
      <div className={STYLES.main}>
        <div
          className={cx(STYLES.slidesContainer, {
            [STYLES.slidesContainerNoNudgers]: images.length === 1,
          })}
        >
          <Nudger onClick={scrollToPrev} label={translations.previousImage}>
            <RtlSupportedChevronLeftIcon />
          </Nudger>
          <div
            className={STYLES.aspectRatio}
            // override aspect ratio if provided, for 16/9 pictures on destination pages only
            style={aspectRatio ? { aspectRatio } : undefined}
          >
            <div className={STYLES.mainScrollContainer}>
              <ScrollContainer
                images={images}
                onVisible={updateShownImageIndex}
                imagesRef={imagesRef}
              />
              <BpkBadge
                type={BADGE_TYPES.strong}
                className={STYLES.counter}
                data-testid="image-gallery-indicator"
              >
                {`${shownImageIndex + 1}/${images.length}`}
              </BpkBadge>
            </div>
          </div>
          <Nudger onClick={scrollToNext} label={translations.nextImage}>
            <RtlSupportedChevronRightIcon />
          </Nudger>
          <div className={STYLES.footer}>
            <BpkBreakpoint query={BREAKPOINTS.TABLET}>
              {(isActive: boolean) =>
                isActive ? (
                  <>
                    <SlidesIndicatorMobile
                      length={images.length}
                      activeIndex={shownImageIndex}
                    />
                    <ImageInfo
                      credit={shownImage.credit}
                      description={shownImage.description}
                      location={shownImage.location}
                    />
                  </>
                ) : (
                  <>
                    <ImageInfo
                      credit={shownImage.credit}
                      description={shownImage.description}
                      location={shownImage.location}
                    />
                    <SlidesIndicatorDesktop
                      images={images}
                      shownImageIndex={shownImageIndex}
                      updateShownImageIndex={updateShownImageIndexAndScroll}
                    />
                  </>
                )
              }
            </BpkBreakpoint>
          </div>
        </div>
      </div>
    </div>
  );
};

type NudgerProps = {
  children: ReactNode;
  onClick: MouseEventHandler<HTMLButtonElement>;
  label: string;
};
const Nudger = ({ children, label, onClick }: NudgerProps) => (
  <BpkButton
    aria-label={label}
    title={label}
    iconOnly
    className={STYLES.nudgeButton}
    onClick={onClick}
    type={BUTTON_TYPES.link}
    implicit
  >
    {children}
  </BpkButton>
);

export default SlideshowView;
